import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, Alert, CircularProgress } from '@mui/material';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Map as MapIcon, Launch as LaunchIcon } from '@mui/icons-material';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Global cache for geocoding results to avoid repeated API calls
const geocodingCache = new Map();

// Rate limiting: Track last request time to ensure 1 request per second max
let lastRequestTime = 0;
const MIN_REQUEST_INTERVAL = 1100; // 1.1 seconds to be safe

const AddressMap = ({ street1, street2, city, province, country, postalCode }) => {
  const [coordinates, setCoordinates] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Build full address string for display (including street2 and postal code)
  const buildFullAddress = () => {
    const parts = [];
    if (street1) parts.push(street1);
    if (street2) parts.push(street2);
    if (city) parts.push(city);
    if (province) parts.push(province);
    if (country) parts.push(country);
    if (postalCode) parts.push(postalCode);
    return parts.join(', ');
  };

  // Build address for geocoding (exclude street2 and postal code for better OpenStreetMap results)
  const buildGeocodingAddress = () => {
    const parts = [];
    if (street1) parts.push(street1);
    if (city) parts.push(city);
    if (province) parts.push(province);
    if (country) parts.push(country);
    return parts.join(', ');
  };

  const fullAddress = buildFullAddress(); // For display and Google Maps
  const geocodingAddress = buildGeocodingAddress(); // For OpenStreetMap

  const geocodeAddress = useCallback(async () => {
    if (!geocodingAddress.trim()) {
      setError('No address available for mapping');
      return;
    }

    // Check cache first
    const cacheKey = geocodingAddress.toLowerCase().trim();
    if (geocodingCache.has(cacheKey)) {
      const cachedResult = geocodingCache.get(cacheKey);
      if (cachedResult.coordinates) {
        setCoordinates(cachedResult.coordinates);
        setError('');
      } else {
        setError(cachedResult.error || 'Address not found on map');
        setCoordinates(null);
      }
      return;
    }

    setLoading(true);
    setError('');
    setCoordinates(null);

    try {
      // Rate limiting: Ensure we don't exceed 1 request per second
      const now = Date.now();
      const timeSinceLastRequest = now - lastRequestTime;
      if (timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
        const waitTime = MIN_REQUEST_INTERVAL - timeSinceLastRequest;
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
      lastRequestTime = Date.now();

      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      // Use OpenStreetMap Nominatim API for geocoding with proper headers
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(geocodingAddress)}&limit=1`,
        {
          signal: controller.signal,
          headers: {
            'User-Agent': 'PulseMembership/1.0 (Business Membership Application)', // Required by Nominatim policy
          }
        }
      );
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`Geocoding service error: ${response.status}`);
      }

      const data = await response.json();
      
      if (data && data.length > 0) {
        const { lat, lon } = data[0];
        const coordinates = [parseFloat(lat), parseFloat(lon)];
        
        // Cache successful result
        geocodingCache.set(cacheKey, { coordinates });
        
        setCoordinates(coordinates);
      } else {
        // Cache failed result to avoid retrying
        geocodingCache.set(cacheKey, { error: 'Address not found on map' });
        setError('Address not found on map');
      }
    } catch (err) {
      console.error('Geocoding error:', err);
      
      let errorMessage = 'Unable to locate address on map';
      if (err.name === 'AbortError') {
        errorMessage = 'Map loading timed out. Please try again later.';
      } else if (err.message.includes('429')) {
        errorMessage = 'Too many map requests. Please wait a moment and try again.';
      } else if (err.message.includes('503') || err.message.includes('502')) {
        errorMessage = 'Map service temporarily unavailable. Please try again later.';
      }
      
      // Cache error to avoid immediate retry
      geocodingCache.set(cacheKey, { error: errorMessage });
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [geocodingAddress]);

  useEffect(() => {
    if (geocodingAddress) {
      geocodeAddress();
    }
  }, [geocodingAddress, geocodeAddress]);

  const handleGoogleMapsClick = () => {
    if (fullAddress) {
      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`;
      window.open(googleMapsUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Don't render if no address components
  if (!street1 && !street2 && !city && !province && !country && !postalCode) {
    return null;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', fontWeight: 500 }}>
          <MapIcon sx={{ mr: 1 }} />
          Location
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<LaunchIcon />}
          onClick={handleGoogleMapsClick}
          disabled={!fullAddress}
        >
          Link to Google Maps
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {fullAddress}
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CircularProgress size={16} />
          <Typography variant="body2">Loading map...</Typography>
        </Box>
      )}

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {coordinates && (
        <Box sx={{ height: 300, width: '100%', borderRadius: 1, overflow: 'hidden' }}>
          <MapContainer
            center={coordinates}
            zoom={15}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Marker position={coordinates}>
              <Popup>
                <div>
                  <strong>Member Location</strong>
                  <br />
                  {fullAddress}
                </div>
              </Popup>
            </Marker>
          </MapContainer>
        </Box>
      )}
    </Box>
  );
};

export default AddressMap;
